import Head from 'next/head'
import { useRouter } from 'next/router'

import { ShwEnv } from '@/constants/types'

import { useUser } from '@/client/context/user'
import { PlatformRuntime } from '@/client/services/platform'
import { getCurrentCanonical, getPublicUrl } from '@/client/services/url'
import { WEBSITE_ENV } from '@/client/settings'

import MetaHeaderSplash from './MetaHeaderSplash'

type MetaContent = {
  title?: string | null
  description?: string | null
  image?: string | null
}

const defaultMetaContent = {
  title: 'Free Online Tutoring — Schoolhouse',
  description:
    'Schoolhouse.world is a free online tutoring platform in which anyone, anywhere can receive live help online. Join our free tutoring community!',
  image: 'https://assets.schoolhouse.world/public/open-graph-v1.png',
}

const WEBSITE_PREFIX_MAP = {
  [ShwEnv.TESTING]: null,
  [ShwEnv.DEVELOPMENT]: '[DEV] ',
  [ShwEnv.STAGING]: '[STAGING] ',
  [ShwEnv.PRODUCTION]: '',
}

export type MetaHeaderProps = {
  title?: string
  canonical?: string | null
  ignoreTitleSuffix?: boolean
  meta?: MetaContent
}

export default function MetaHeader({
  title,
  canonical = null,
  ignoreTitleSuffix = false,
  meta = defaultMetaContent,
}: MetaHeaderProps): JSX.Element {
  const router = useRouter()
  const user = useUser()

  const titlePrefix = WEBSITE_PREFIX_MAP[WEBSITE_ENV]

  const metaContent = {
    title: meta.title ?? defaultMetaContent.title,
    description: meta.description ?? defaultMetaContent.description,
    image: meta.image ?? defaultMetaContent.image,
  }

  const generatedTitle = `${titlePrefix}${
    title != null
      ? ignoreTitleSuffix === true
        ? title
        : `${title} | Schoolhouse`
      : 'Free Online Tutoring — Schoolhouse'
  }`

  const viewport =
    user.platformRuntime === PlatformRuntime.ios
      ? // Disable zoom to emulate how real apps feel like
        'initial-scale=1.0, width=device-width, maximum-scale=1.0, user-scalable=no'
      : 'initial-scale=1.0, width=device-width'

  return (
    <Head>
      <meta name="viewport" content={viewport} />
      <meta name="description" content={metaContent.description} />
      <meta name="theme-color" content="#2B9C79" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />

      <meta property="og:title" content={metaContent.title} />
      <meta property="og:description" content={metaContent.description} />
      <meta property="og:image" content={metaContent.image} />
      <meta property="og:url" content={canonical ?? getCurrentCanonical(router)} />
      <meta property="og:type" content="website" />

      <meta name="twitter:title" content={metaContent.title} />
      <meta name="twitter:description" content={metaContent.description} />
      <meta name="twitter:image" content={metaContent.image} />
      <meta name="twitter:card" content="summary_large_image" />

      <link rel="canonical" href={canonical ?? getCurrentCanonical(router)} />
      <link rel="icon" href={getPublicUrl('pwa/logo-192.png')} />
      <MetaHeaderSplash />

      <title>{generatedTitle}</title>
    </Head>
  )
}
